import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Palette, Image as ImageIcon, Upload, Loader2, X, Smartphone, Download, Sparkles, Check, CreditCard, KeyRound, Eye, RefreshCw, AlertTriangle } from "lucide-react";
import { useBranding } from "../../context/BrandingContext";
import { THEME_PRESETS, DEFAULT_PRESET, getPreset } from "../../theme/presets";
import { resolveTenantSlug } from "../../lib/api";

const MISSING_LABEL = { key_id: "Key ID", key_secret: "Key Secret" };

/** What customers get at checkout right now, and exactly why. */
function PaymentStatus({ status, checkoutOrigin = window.location.origin }) {
  if (!status) return null;
  if (!status.available) {
    const missing = status.missing.map((m) => MISSING_LABEL[m] || m).join(" and ");
    return (
      <div className="mb-4 border border-red-200 bg-red-50 text-red-800 text-sm p-3 flex gap-2" data-testid="razorpay-status" data-available="false">
        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
        <div className="min-w-0 space-y-1">
          <div className="font-medium">Online payments are OFF — customers only see Cash on Delivery.</div>
          <div data-testid="razorpay-status-missing">Missing here: {missing}. Enter {status.missing.length > 1 ? "both" : "it"} below and press Save payment keys.</div>
          {status.platform_keys_entered && (
            <div data-testid="razorpay-status-platform-keys">
              You have entered Razorpay keys under <strong>Settings → Platform Razorpay</strong>. Those are only used for
              platform fees, not for customer checkout. Enter your keys here instead.
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="mb-4 border border-green-200 bg-green-50 text-green-800 text-sm p-3 space-y-1" data-testid="razorpay-status" data-available="true">
      <div className="font-medium flex items-center gap-2"><Check size={16} className="shrink-0" />
        Online payments are ON for customers{status.test_mode ? " — Test mode (no real money)" : " — Live"}.
      </div>
      {!status.test_mode && (
        <div className="text-green-900" data-testid="razorpay-status-website">
          Razorpay only accepts live payments from websites registered on your Razorpay account. Make sure{" "}
          <strong className="break-all" data-testid="razorpay-status-website-origin">{checkoutOrigin}</strong> is listed
          in the Razorpay Dashboard under Account &amp; Settings → Website and app details — your mobile app pays
          through the same website. If it is missing, UPI payments fail with “website does not match registered
          website(s)”.
        </div>
      )}
      {!status.webhook_secret_set && (
        <div className="text-amber-800" data-testid="razorpay-status-webhook-missing">
          Webhook Secret not set: a payment is recorded at once when the customer returns to your store, but if
          they close the page first it is only picked up by the automatic check, up to 30 minutes later. Add the
          Webhook Secret below.
        </div>
      )}
    </div>
  );
}

export default function AdminBranding() {
  const { branding: current, refresh } = useBranding();
  const [form, setForm] = useState({
    brand_name: "",
    short_name: "",
    tagline: "",
    logo_square_url: "",
    logo_horizontal_url: "",
    header_text: "",
    header_text_font: "display",
    header_text_size: "lg",
    contact_whatsapp: "",
    careers_whatsapp: "",
    accent_color: "#7c3a4b",
    theme_color: "#7c3a4b",
    background_color: "#ffffff",
    theme_preset: DEFAULT_PRESET,
    font_pair: "",
    razorpay_key_id: "",
    razorpay_key_secret: "",
    razorpay_webhook_secret: "",
    low_stock_indicator_enabled: false,
    low_stock_threshold: 5,
  });
  // Per-preset colour overrides — "Edit" button on each theme card.
  const [themeColors, setThemeColors] = useState({ primary: null, secondary: null, background: null });
  const [editingColorsFor, setEditingColorsFor] = useState(null);
  const [savingColors, setSavingColors] = useState(false);
  const [razorpaySecretSet, setRazorpaySecretSet] = useState(false);
  const [webhookSecretSet, setWebhookSecretSet] = useState(false);
  // Server's view of whether customers can pay online right now (same check
  // checkout uses) and, if not, what is missing. Never contains secrets.
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [savingPayments, setSavingPayments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const tenantSlug = resolveTenantSlug();
  const previewSrc = `/t/${tenantSlug}/?preview=${previewKey}`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/branding");
        setForm({
          brand_name: data.brand_name || "",
          short_name: data.short_name || "",
          tagline: data.tagline || "",
          logo_square_url: data.logo_square_url || "",
          logo_horizontal_url: data.logo_horizontal_url || "",
          header_text: data.header_text || "",
          header_text_font: data.header_text_font || "display",
          header_text_size: data.header_text_size || "lg",
          contact_whatsapp: data.contact_whatsapp || "",
          careers_whatsapp: data.careers_whatsapp || "",
          accent_color: data.accent_color || "#7c3a4b",
          theme_color: data.theme_color || "#7c3a4b",
          background_color: data.background_color || "#ffffff",
          theme_preset: data.theme_preset || DEFAULT_PRESET,
          font_pair: data.font_pair || "",
          razorpay_key_id: data.razorpay_key_id || "",
          razorpay_key_secret: "",
          razorpay_webhook_secret: "",
          low_stock_indicator_enabled: !!data.low_stock_indicator_enabled,
          low_stock_threshold: Number.isFinite(Number(data.low_stock_threshold))
            ? Number(data.low_stock_threshold)
            : 5,
        });
        setRazorpaySecretSet(!!data.razorpay_key_secret_set);
        setWebhookSecretSet(!!data.razorpay_webhook_secret_set);
        setPaymentStatus(data.online_payments || null);
        setThemeColors({
          primary: data.theme_color_overrides?.primary || null,
          secondary: data.theme_color_overrides?.secondary || null,
          background: data.theme_color_overrides?.background || null,
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load settings");
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const save = async (overrides = {}) => {
    const next = { ...form, ...overrides };
    if (!next.brand_name.trim()) return toast.error("Brand name is required");
    setSaving(true);
    try {
      const payload = {
        brand_name: next.brand_name.trim(),
        short_name: next.short_name.trim() || null,
        tagline: next.tagline.trim() || null,
        logo_square_url: (next.logo_square_url || "").trim() || null,
        logo_horizontal_url: (next.logo_horizontal_url || "").trim() || null,
        header_text: (next.header_text || "").trim() || null,
        header_text_font: next.header_text_font || null,
        header_text_size: next.header_text_size || null,
        contact_whatsapp: (next.contact_whatsapp || "").trim() || null,
        careers_whatsapp: (next.careers_whatsapp || "").trim() || null,
        accent_color: next.accent_color || null,
        theme_color: next.theme_color || null,
        background_color: next.background_color || null,
        theme_preset: next.theme_preset || DEFAULT_PRESET,
        font_pair: next.font_pair || null,
        low_stock_indicator_enabled: !!next.low_stock_indicator_enabled,
        low_stock_threshold: (() => {
          const n = Number(next.low_stock_threshold);
          return Number.isFinite(n) && n >= 1 && n <= 999 ? n : 5;
        })(),
      };
      await api.put("/admin/branding", payload);
      toast.success("Branding updated");
      await refresh();
      // Hard-refresh the live preview iframe so the customer-facing storefront
      // re-bootstraps with the new branding/theme tokens.
      setPreviewKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveThemeColors = async (overrides = {}) => {
    const next = { ...themeColors, ...overrides };
    setThemeColors(next);
    setSavingColors(true);
    try {
      await api.put("/admin/branding/theme-colors", next);
      toast.success("Theme colours updated");
      await refresh();
      setPreviewKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingColors(false);
    }
  };

  const resetThemeColors = () => saveThemeColors({ primary: null, secondary: null, background: null });

  const savePaymentKeys = async () => {
    setSavingPayments(true);
    try {
      const payload = {
        brand_name: form.brand_name.trim() || current?.brand_name || "Store",
        razorpay_key_id: form.razorpay_key_id.trim() || null,
      };
      if (form.razorpay_key_secret) payload.razorpay_key_secret = form.razorpay_key_secret;
      if (form.razorpay_webhook_secret) payload.razorpay_webhook_secret = form.razorpay_webhook_secret;
      await api.put("/admin/branding", payload);
      toast.success("Razorpay keys saved");
      api.get("/admin/branding").then(({ data }) => setPaymentStatus(data.online_payments || null)).catch(() => {});
      setForm({ ...form, razorpay_key_secret: "", razorpay_webhook_secret: "" });
      setRazorpaySecretSet(!!(form.razorpay_key_secret || razorpaySecretSet));
      setWebhookSecretSet(!!(form.razorpay_webhook_secret || webhookSecretSet));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingPayments(false);
    }
  };

  /**
   * Update a logo field AND immediately persist it. Called by LogoUploader
   * after both an upload (new URL) and a remove (empty string). Without
   * this auto-save, users frequently click the X expecting it to "remove"
   * the logo, then leave the page without clicking Save and the old logo
   * stays live in production.
   */
  const setLogoAndPersist = async (key, url) => {
    setForm((f) => ({ ...f, [key]: url || "" }));
    await save({ [key]: url || "" });
  };

  const exportBundle = async () => {
    setExporting(true);
    try {
      const { data, headers } = await api.get("/admin/branding/export.zip", {
        responseType: "blob",
      });
      // Parse filename from Content-Disposition (falls back to a safe default)
      const disp = headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(disp);
      const filename = m ? m[1] : "branding.zip";
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Bundle exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!loaded) {
    return <div className="p-8 text-center text-[hsl(var(--kb-muted))]">Loading…</div>;
  }

  return (
    <div data-testid="admin-branding-page" className="max-w-7xl">
      <div className="mb-8">
        <div className="overline mb-1">Settings</div>
        <h1 className="font-display text-3xl md:text-4xl">Branding & PWA</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-xl">
          Rebrand the storefront end-to-end. Your brand name and logo power the header, footer, emails, and the Install-to-Home-Screen (PWA) experience.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_460px] gap-6 items-start">
        <div className="space-y-6 bg-white border border-[hsl(var(--kb-border))] p-6">
        <section>
          <h2 className="font-display text-xl mb-4">Brand identity</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Brand name">
              <input
                type="text"
                value={form.brand_name}
                onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                className="kb-input"
                maxLength={60}
                data-testid="branding-name"
              />
            </Field>
            <Field label="Short name (PWA, ≤12 chars)">
              <input
                type="text"
                value={form.short_name}
                onChange={(e) => setForm({ ...form, short_name: e.target.value })}
                className="kb-input"
                maxLength={12}
                placeholder="Auto-trimmed from brand name"
                data-testid="branding-short-name"
              />
            </Field>
            <Field label="Tagline" className="md:col-span-2">
              <input
                type="text"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                className="kb-input"
                maxLength={120}
                placeholder="Shown in footer and PWA description"
                data-testid="branding-tagline"
              />
            </Field>
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><ImageIcon size={18} /> Logos</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Upload a square logo (≥512×512) for the PWA/favicon, plus an optional horizontal logo for the header.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <LogoUploader
              label="Square logo"
              testid="square"
              value={form.logo_square_url}
              onChange={(url) => setLogoAndPersist("logo_square_url", url)}
              aspectClass="aspect-square"
              hint="Used as favicon + PWA install icon (512×512 ideal). Saves automatically on upload/remove."
            />
            <LogoUploader
              label="Horizontal logo (optional)"
              testid="horizontal"
              value={form.logo_horizontal_url}
              onChange={(url) => setLogoAndPersist("logo_horizontal_url", url)}
              aspectClass="aspect-[4/1]"
              hint="Shown in the store header. Pair it with the optional text below for a side-by-side wordmark."
            />
          </div>

          <HeaderTextEditor
            form={form}
            setForm={setForm}
            onCommit={(patch) => save(patch)}
          />

          <div
            className="mt-6 pt-6 border-t border-dashed border-[hsl(var(--kb-border))]"
            data-testid="footer-whatsapp-editor"
          >
            <div className="flex items-baseline justify-between mb-2 gap-2">
              <h3 className="font-medium text-sm">Footer WhatsApp links</h3>
              <span className="text-[11px] text-[hsl(var(--kb-muted))]">
                Leave blank to hide WhatsApp
              </span>
            </div>
            <p className="text-[11px] text-[hsl(var(--kb-muted))] mb-4">
              When set, the storefront footer's <b>Contact</b> and{" "}
              <b>Careers</b> entries become clickable WhatsApp chats with a
              pre-filled message that mentions your brand. Enter the phone
              number with country code (e.g. <code>+919876543210</code>) —
              we strip everything but digits before building the link.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="text-xs flex flex-col">
                <span className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">
                  Contact WhatsApp
                </span>
                <input
                  className="kb-input"
                  type="tel"
                  value={form.contact_whatsapp}
                  onChange={(e) => setForm({ ...form, contact_whatsapp: e.target.value })}
                  onBlur={() => save({ contact_whatsapp: (form.contact_whatsapp || "").trim() || null })}
                  placeholder="+91 98765 43210"
                  maxLength={24}
                  data-testid="contact-whatsapp-input"
                />
              </label>
              <label className="text-xs flex flex-col">
                <span className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">
                  Careers WhatsApp
                </span>
                <input
                  className="kb-input"
                  type="tel"
                  value={form.careers_whatsapp}
                  onChange={(e) => setForm({ ...form, careers_whatsapp: e.target.value })}
                  onBlur={() => save({ careers_whatsapp: (form.careers_whatsapp || "").trim() || null })}
                  placeholder="+91 98765 43210"
                  maxLength={24}
                  data-testid="careers-whatsapp-input"
                />
              </label>
            </div>
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Sparkles size={18} /> Storefront theme</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Pick a preset and your customers' homepage, header, buttons and typography swap instantly. The custom colours below act as <em>overrides</em> on top of the chosen preset.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="theme-preset-grid">
            {Object.entries(THEME_PRESETS).map(([key, p]) => {
              const active = form.theme_preset === key;
              return (
                <div key={key} className="relative">
                  <button
                    type="button"
                    onClick={() => { setForm({ ...form, theme_preset: key, font_pair: "" }); save({ theme_preset: key, font_pair: null }); }}
                    className={`w-full text-left border p-4 transition relative ${active ? "border-[hsl(var(--kb-primary))] ring-2 ring-[hsl(var(--kb-primary))]/20" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/40"}`}
                    data-testid={`theme-preset-${key}`}
                  >
                    {active && (
                      <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[hsl(var(--kb-primary))] text-white flex items-center justify-center" data-testid={`theme-preset-${key}-active`}>
                        <Check size={12} />
                      </span>
                    )}
                    <div className="flex items-center gap-1 mb-3">
                      {p.swatches.map((c, i) => (
                        <span key={i} className="w-6 h-6 rounded-full border border-black/10" style={{ background: c }} />
                      ))}
                    </div>
                    <div className="font-medium pr-14">{p.label}</div>
                    <p className="text-xs text-[hsl(var(--kb-muted))] mt-1 leading-relaxed">{p.description}</p>
                  </button>
                  {/* Sibling, not nested — a <button> can't contain another
                     <button>. Selects the preset (if not already active)
                     and opens the colour editor below. */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!active) { setForm({ ...form, theme_preset: key, font_pair: "" }); save({ theme_preset: key, font_pair: null }); }
                      setEditingColorsFor(key);
                    }}
                    className="absolute bottom-3 right-3 text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] inline-flex items-center gap-1 px-2 py-1 border border-[hsl(var(--kb-border))] bg-white"
                    data-testid={`theme-preset-${key}-edit`}
                    title={`Edit ${p.label} colours`}
                  >
                    <Palette size={10} /> Edit
                  </button>
                </div>
              );
            })}
          </div>

          {editingColorsFor && (
            <ThemeColorEditor
              presetKey={editingColorsFor}
              preset={getPreset(editingColorsFor)}
              colors={themeColors}
              saving={savingColors}
              onChange={(patch) => saveThemeColors(patch)}
              onReset={resetThemeColors}
              onClose={() => setEditingColorsFor(null)}
            />
          )}
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]" data-testid="typography-section">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Sparkles size={18} /> Typography</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Four hand-picked font pairings tuned for the <b>{getPreset(form.theme_preset).label}</b> theme.
            The first one is our recommendation — click any card to switch instantly.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="font-pair-grid">
            {(getPreset(form.theme_preset).fontPairs || []).map((pair, idx) => {
              const isActive = (form.font_pair || (getPreset(form.theme_preset).fontPairs?.[0]?.key)) === pair.key;
              return (
                <button
                  key={pair.key}
                  type="button"
                  onClick={() => { setForm({ ...form, font_pair: pair.key }); save({ font_pair: pair.key }); }}
                  className={`text-left border p-4 transition relative ${isActive ? "border-[hsl(var(--kb-primary))] ring-2 ring-[hsl(var(--kb-primary))]/20" : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/40"}`}
                  data-testid={`font-pair-${pair.key}`}
                >
                  {isActive && (
                    <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[hsl(var(--kb-primary))] text-white flex items-center justify-center" data-testid={`font-pair-${pair.key}-active`}>
                      <Check size={12} />
                    </span>
                  )}
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl leading-none" style={{ fontFamily: pair.display }}>Aa</span>
                    <span className="text-base leading-none text-[hsl(var(--kb-muted))]" style={{ fontFamily: pair.body }}>aa</span>
                  </div>
                  <div className="font-medium text-sm">{pair.label}{idx === 0 && <span className="ml-2 text-[10px] uppercase tracking-wider text-[hsl(var(--kb-primary))]">Recommended</span>}</div>
                  <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1 leading-snug">{pair.note}</p>
                  <div className="mt-3 text-[10px] text-[hsl(var(--kb-muted))] font-mono break-all">
                    {pair.display.split(",")[0].replace(/['"]/g, "")} / {pair.body.split(",")[0].replace(/['"]/g, "")}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]">
          <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Palette size={18} /> Colours</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <ColorField label="Accent" value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v })} testid="accent" />
            <ColorField label="PWA theme colour" value={form.theme_color} onChange={(v) => setForm({ ...form, theme_color: v })} testid="theme" />
            <ColorField label="PWA splash background" value={form.background_color} onChange={(v) => setForm({ ...form, background_color: v })} testid="bg" />
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]" data-testid="low-stock-section">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2">
            <AlertTriangle size={18} /> Low-stock urgency
          </h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Show a small &ldquo;Only N left in stock&rdquo; pill on the product page when a variant is running low. Nudges buyers to check out sooner and improves conversion.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <label className="flex items-start gap-3 cursor-pointer flex-1" data-testid="low-stock-enabled-toggle">
              <input
                type="checkbox"
                className="mt-1 w-4 h-4 accent-[hsl(var(--kb-primary))]"
                checked={!!form.low_stock_indicator_enabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setForm({ ...form, low_stock_indicator_enabled: enabled });
                  save({ low_stock_indicator_enabled: enabled });
                }}
                data-testid="low-stock-enabled-input"
              />
              <span className="text-sm leading-snug">
                <span className="block font-medium">Enable urgency pill</span>
                <span className="block text-xs text-[hsl(var(--kb-muted))] mt-0.5">
                  Off by default. Turn on to display low-stock urgency on the storefront.
                </span>
              </span>
            </label>
            <label className="text-xs flex flex-col w-full sm:w-48">
              <span className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">
                Threshold
              </span>
              <input
                type="number"
                min={1}
                max={999}
                className="kb-input"
                value={form.low_stock_threshold}
                onChange={(e) =>
                  setForm({ ...form, low_stock_threshold: e.target.value })
                }
                onBlur={() => {
                  const n = Number(form.low_stock_threshold);
                  const clamped = Number.isFinite(n) && n >= 1 && n <= 999 ? n : 5;
                  setForm({ ...form, low_stock_threshold: clamped });
                  save({ low_stock_threshold: clamped });
                }}
                disabled={!form.low_stock_indicator_enabled}
                data-testid="low-stock-threshold-input"
              />
              <span className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">
                Pill shows when 1 ≤ stock ≤ threshold.
              </span>
            </label>
          </div>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]" data-testid="razorpay-section">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><CreditCard size={18} /> Payments — Razorpay</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            Enter your own Razorpay credentials so customer payments land directly in your Razorpay account.
            Until both the Key ID and Key Secret are saved, customers can only use Cash on Delivery.
            Use <code>rzp_test_</code> keys to try payments without real money.
            <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="ml-1 underline text-[hsl(var(--kb-primary))]">Get your keys</a>
          </p>
          <PaymentStatus status={paymentStatus} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="text-xs flex flex-col">
              <span className="flex items-center gap-1 mb-1"><KeyRound size={11} /> Key ID</span>
              <input
                className="kb-input"
                value={form.razorpay_key_id}
                onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })}
                placeholder="rzp_test_xxxxxxxxxxxx or rzp_live_xxxx"
                data-testid="razorpay-key-id-input"
              />
            </label>
            <label className="text-xs flex flex-col">
              <span className="flex items-center gap-1 mb-1">
                <KeyRound size={11} /> Key Secret
                {razorpaySecretSet && !form.razorpay_key_secret && (
                  <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full" data-testid="razorpay-secret-status">
                    saved · enter to update
                  </span>
                )}
              </span>
              <input
                type="password"
                className="kb-input"
                value={form.razorpay_key_secret}
                onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })}
                placeholder={razorpaySecretSet ? "•••••••••• (configured)" : "••••••••••••••••"}
                data-testid="razorpay-key-secret-input"
              />
            </label>
            <label className="text-xs flex flex-col md:col-span-2">
              <span className="flex items-center gap-1 mb-1">
                <KeyRound size={11} /> Webhook Secret
                {webhookSecretSet && !form.razorpay_webhook_secret && (
                  <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full" data-testid="razorpay-webhook-secret-status">
                    saved · enter to update
                  </span>
                )}
              </span>
              <input
                type="password"
                className="kb-input"
                autoComplete="new-password"
                value={form.razorpay_webhook_secret}
                onChange={(e) => setForm({ ...form, razorpay_webhook_secret: e.target.value })}
                placeholder={webhookSecretSet ? "•••••••••• (configured)" : "The secret you set on the webhook in Razorpay"}
                data-testid="razorpay-webhook-secret-input"
              />
              <span className="text-[11px] text-[hsl(var(--kb-muted))] mt-1" data-testid="razorpay-webhook-secret-help">
                Razorpay Dashboard → Webhooks → your webhook → Secret. This is NOT your Key Secret. Without it,
                Razorpay&apos;s payment notifications are rejected: payments are then recorded when the customer returns
                to your store, or by the automatic check up to 30 minutes later.
              </span>
            </label>
          </div>
          <button onClick={savePaymentKeys} disabled={savingPayments}
                  className="kb-btn-primary mt-4 text-sm inline-flex items-center gap-2"
                  data-testid="razorpay-save-btn">
            {savingPayments ? <Loader2 size={12} className="animate-spin" /> : null}
            Save payment keys
          </button>
        </section>

        <section className="pt-6 border-t border-[hsl(var(--kb-border))]">
          <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Smartphone size={18} /> PWA preview</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
            When a customer taps "Install app" in their browser, they'll see the icon + name below.
          </p>
          <div className="inline-flex items-center gap-4 border border-[hsl(var(--kb-border))] p-4 rounded-xl bg-[hsl(var(--kb-surface-alt))]" data-testid="branding-pwa-preview">
            <div
              className="w-14 h-14 rounded-xl overflow-hidden shadow-md"
              style={{ backgroundColor: form.background_color || "#fff" }}
            >
              {form.logo_square_url ? (
                <img src={form.logo_square_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[hsl(var(--kb-muted))]">
                  <ImageIcon size={20} />
                </div>
              )}
            </div>
            <div>
              <div className="font-medium">{form.short_name || form.brand_name || "Brand"}</div>
              <div className="text-xs text-[hsl(var(--kb-muted))]">{form.tagline || "Tagline"}</div>
            </div>
          </div>
        </section>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pt-4 border-t border-[hsl(var(--kb-border))]">
          <button
            onClick={exportBundle}
            disabled={exporting}
            className="kb-btn-outline inline-flex items-center gap-2"
            data-testid="branding-export-btn"
            title="Download a zip with manifest, icons, theme tokens & README"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exporting ? "Packing…" : "Export white-label bundle"}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="kb-btn-primary"
            data-testid="branding-save-btn"
          >
            {saving ? "Saving…" : "Save branding"}
          </button>
        </div>

        <div className="text-[11px] text-[hsl(var(--kb-muted))] pt-2">
          Current on file: <span className="font-mono">{current?.brand_name}</span> · last updated {current?.updated_at ? new Date(current.updated_at).toLocaleString() : "never"}
        </div>
      </div>

      {/* Live storefront preview — re-mounts iframe whenever branding is saved */}
      <aside
        className="bg-white border border-[hsl(var(--kb-border))] p-4 lg:sticky lg:top-6 self-start"
        data-testid="branding-live-preview"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-medium text-sm">
            <Eye size={14} /> Live preview
          </div>
          <button
            type="button"
            onClick={() => setPreviewKey((k) => k + 1)}
            className="text-xs text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] inline-flex items-center gap-1"
            data-testid="branding-preview-refresh"
            aria-label="Refresh preview"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <p className="text-[11px] text-[hsl(var(--kb-muted))] mb-3 leading-relaxed">
          Your storefront with the currently saved branding. Saves auto-refresh this view.
        </p>
        <div className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] overflow-hidden" style={{ aspectRatio: "9 / 14" }}>
          <iframe
            key={previewKey}
            title="Storefront preview"
            src={previewSrc}
            className="w-full h-full"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            data-testid="branding-preview-iframe"
          />
        </div>
        <div className="text-[10px] text-[hsl(var(--kb-muted))] mt-2 font-mono break-all">
          {previewSrc}
        </div>
      </aside>
      </div>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block text-sm ${className}`}>
      <div className="mb-1 text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wider">{label}</div>
      {children}
    </label>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2 border border-[hsl(var(--kb-border))] px-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 bg-transparent border-0 cursor-pointer"
          data-testid={`branding-color-${testid}`}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 py-2 text-sm font-mono focus:outline-none"
          maxLength={7}
          pattern="^#[0-9a-fA-F]{6}$"
        />
      </div>
    </Field>
  );
}

/**
 * Inline colour editor opened by the "Edit" button on a theme preset
 * card. Local-only state until "Save colours" is clicked (matches the
 * native colour picker firing onChange continuously while dragging —
 * saving on every tick would spam the API). Pre-fills each field from
 * the preset's own swatch when no override is set yet, so the picker
 * starts from a sensible colour instead of blank/black.
 */
function ThemeColorEditor({ presetKey, preset, colors, saving, onChange, onReset, onClose }) {
  const defaults = {
    primary: preset.swatches[0],
    secondary: preset.swatches[1],
    background: preset.swatches[2],
  };
  const [local, setLocal] = useState({
    primary: colors.primary || defaults.primary,
    secondary: colors.secondary || defaults.secondary,
    background: colors.background || defaults.background,
  });
  useEffect(() => {
    setLocal({
      primary: colors.primary || defaults.primary,
      secondary: colors.secondary || defaults.secondary,
      background: colors.background || defaults.background,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey, colors.primary, colors.secondary, colors.background]);

  const hasOverrides = !!(colors.primary || colors.secondary || colors.background);

  return (
    <div
      className="mt-4 border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] p-5"
      data-testid="theme-color-editor"
    >
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <Palette size={14} /> Edit colours — {preset.label}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
          data-testid="theme-color-editor-close"
          aria-label="Close colour editor"
        >
          <X size={16} />
        </button>
      </div>
      <p className="text-xs text-[hsl(var(--kb-muted))] mb-4">
        Overrides this preset's Primary, Secondary and Background colours across the storefront. Leave as-is to keep the preset's original look.
      </p>
      <div className="grid sm:grid-cols-3 gap-4">
        <ColorField label="Primary" value={local.primary} onChange={(v) => setLocal({ ...local, primary: v })} testid="theme-primary" />
        <ColorField label="Secondary" value={local.secondary} onChange={(v) => setLocal({ ...local, secondary: v })} testid="theme-secondary" />
        <ColorField label="Background" value={local.background} onChange={(v) => setLocal({ ...local, background: v })} testid="theme-background" />
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button
          type="button"
          onClick={() => onChange(local)}
          disabled={saving}
          className="kb-btn-primary text-sm inline-flex items-center gap-2"
          data-testid="theme-color-editor-save"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : null}
          Save colours
        </button>
        {hasOverrides && (
          <button
            type="button"
            onClick={onReset}
            disabled={saving}
            className="kb-btn-outline text-sm"
            data-testid="theme-color-editor-reset"
          >
            Reset to preset defaults
          </button>
        )}
      </div>
    </div>
  );
}

function LogoUploader({ label, value, onChange, aspectClass, hint, testid }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) onChange(data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wider mb-2">{label}</div>
      <div className={`relative ${aspectClass} border border-dashed border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] overflow-hidden`} data-testid={`branding-logo-${testid}`}>
        {value ? (
          <>
            <img src={value} alt="" className="w-full h-full object-contain p-2" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 w-7 h-7 bg-black/60 text-white rounded-full flex items-center justify-center"
              data-testid={`branding-logo-${testid}-remove`}
              aria-label="Remove logo"
            >
              <X size={14} />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="absolute inset-0 flex flex-col items-center justify-center text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
            data-testid={`branding-logo-${testid}-upload`}
          >
            {uploading ? <Loader2 size={24} className="animate-spin" /> : <Upload size={24} />}
            <span className="text-xs uppercase tracking-widest mt-2">
              {uploading ? "Uploading…" : "Click to upload"}
            </span>
          </button>
        )}
      </div>
      <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-2">{hint}</p>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Header text editor — text + font + size for the wordmark next to the logo.
// Exposed as constants so StoreHeader.jsx + BrandingContext can agree on the
// available font keys / size scale.
// ---------------------------------------------------------------------------
export const HEADER_TEXT_FONTS = [
  { key: "display", label: "Theme display font", css: "var(--font-display)" },
  { key: "body",    label: "Theme body font",    css: "var(--font-body)" },
  { key: "serif",   label: "Serif",              css: "'Playfair Display', Georgia, serif" },
  { key: "sans",    label: "Sans",               css: "'Inter', system-ui, sans-serif" },
  { key: "mono",    label: "Mono",               css: "'JetBrains Mono', ui-monospace, monospace" },
];

export const HEADER_TEXT_SIZES = [
  { key: "sm",  label: "Small",      css: "0.875rem" },
  { key: "md",  label: "Medium",     css: "1rem" },
  { key: "lg",  label: "Large",      css: "1.25rem" },
  { key: "xl",  label: "Extra large", css: "1.5rem" },
  { key: "2xl", label: "Huge",       css: "1.875rem" },
];

function fontCss(key) {
  return (HEADER_TEXT_FONTS.find((f) => f.key === key) || HEADER_TEXT_FONTS[0]).css;
}
function sizeCss(key) {
  return (HEADER_TEXT_SIZES.find((s) => s.key === key) || HEADER_TEXT_SIZES[2]).css;
}

function HeaderTextEditor({ form, setForm, onCommit }) {
  const fontKey = form.header_text_font || "display";
  const sizeKey = form.header_text_size || "lg";
  const previewFont = fontCss(fontKey);
  const previewSize = sizeCss(sizeKey);

  return (
    <div
      className="mt-6 pt-6 border-t border-dashed border-[hsl(var(--kb-border))]"
      data-testid="header-text-editor"
    >
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <h3 className="font-medium text-sm">Header text next to logo</h3>
        <span className="text-[11px] text-[hsl(var(--kb-muted))]">
          Leave blank to show logo only
        </span>
      </div>
      <p className="text-[11px] text-[hsl(var(--kb-muted))] mb-4">
        Renders inline beside the logo on the storefront header — handy when
        your logo is an icon mark and you still want the brand wordmark to
        sit next to it.
      </p>

      <div className="grid md:grid-cols-[1fr_220px_180px] gap-3 items-end">
        <label className="text-xs flex flex-col">
          <span className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">
            Text
          </span>
          <input
            type="text"
            value={form.header_text}
            onChange={(e) => setForm({ ...form, header_text: e.target.value })}
            onBlur={() => onCommit({ header_text: form.header_text.trim() })}
            placeholder="e.g. Local Appstore"
            maxLength={60}
            className="kb-input"
            data-testid="header-text-input"
          />
        </label>
        <label className="text-xs flex flex-col">
          <span className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">
            Font
          </span>
          <select
            value={fontKey}
            onChange={(e) => {
              setForm({ ...form, header_text_font: e.target.value });
              onCommit({ header_text_font: e.target.value });
            }}
            className="kb-input"
            data-testid="header-text-font"
          >
            {HEADER_TEXT_FONTS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
        </label>
        <label className="text-xs flex flex-col">
          <span className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">
            Size
          </span>
          <select
            value={sizeKey}
            onChange={(e) => {
              setForm({ ...form, header_text_size: e.target.value });
              onCommit({ header_text_size: e.target.value });
            }}
            className="kb-input"
            data-testid="header-text-size"
          >
            {HEADER_TEXT_SIZES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Live preview: logo + text wordmark */}
      <div
        className="mt-4 inline-flex items-center gap-3 border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] px-4 py-3"
        data-testid="header-text-preview"
      >
        {(form.logo_horizontal_url || form.logo_square_url) && (
          <img
            src={form.logo_horizontal_url || form.logo_square_url}
            alt=""
            className="h-8 w-auto object-contain"
          />
        )}
        {form.header_text && form.header_text.trim() ? (
          <span style={{ fontFamily: previewFont, fontSize: previewSize, lineHeight: 1.1 }}>
            {form.header_text}
          </span>
        ) : (
          <span className="text-[11px] text-[hsl(var(--kb-muted))] italic">
            No header text — logo will appear alone.
          </span>
        )}
      </div>
    </div>
  );
}
